import React from 'react'
import Select from 'react-select'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { styled } from '@mui/material/styles'
import COLUMNS_RGA from '../../store/objects/data/rga_columns.json'
import { useDispatch, useSelector } from 'react-redux'
import { setOptionalMappings, removeOptional } from '../../store/pages/mapping'
import { SingleValue, SingleValueStyles } from '../../components/sql/SelectSingleValue'
import MessageDialog from '../../components/dialogs/MessageDialog'
import MappingListRow from './MappingListRow'

const EMPTY_ARRAY = Object.freeze([])

const STUDY_TYPE = {
  redi: 'REDI',
  skipMap: 'REDI'
}

const FILE_TYPE = {
  I: 'inforce',
  T: 'termination',
  IT: 'inforce+termination'
}

const OPTION_UNMAPPED = {
  value: 'Unmapped',
  label: 'Unmapped'
}

const EMPTY_ROW_ELEMENT = (
  <TableRow>
    <TableCell>
    </TableCell>
    <TableCell>
    </TableCell>
    <TableCell>
    </TableCell>
  </TableRow>
)

const mapDataType = rgaColumnType => {
  const type = rgaColumnType.toUpperCase()
  if (type.toUpperCase().includes('NUMBER')) {
    return 'decimal'
  }

  if (type.toUpperCase().includes('DATE')) {
    return 'date'
  }

  return 'varchar'
}

const StyledTableCell = styled(TableCell)(({
  minWidth: '180px'
}))

function CoreRows ({
  access,
  guid,
  fileType,
  fileData,
  mappingColumns,
  setSourceFocus
}) {
  const file = fileData?.find(file => file.guid === guid)

  if (file === undefined) {
    return ''
  }

  const baseSourceColumnOptions = file.columns.map((column, index) => ({
    value: index.toString(),
    label: column.Name,
    sourceDataType: column.Type
  }))

  const sourceColumnOptions = [
    ...baseSourceColumnOptions,
    OPTION_UNMAPPED
  ]

  return mappingColumns.map((fileColumn, index) => {
    return <MappingListRow
      key={index}
      access={access}
      guid={guid}
      fileColumn={fileColumn}
      fileType={fileType}
      index={index}
      setSourceFocus={setSourceFocus}
      sourceColumnOptions={sourceColumnOptions}
    />
  })
}

function OptionalRows ({
  access,
  guid,
  fileType,
  columnCount,
  options,
  fileMappingsLength
}) {
  const dispatch = useDispatch()
  const [openDialog, setOpenDialog] = React.useState(false)
  const [newOptional, setNewOptional] = React.useState(null)
  const DIALOG_TITLE = 'Notification'
  const DIALOG_TEXT = `The field that was just added is part of the unique key. This field is used to define a unique policy
  and therefore should be consistent between all files. This field has been added to each file.`

  return [...Array(columnCount).keys()].map((key, index) => {
    const handleRemove = () => {
      dispatch(removeOptional(fileType, guid, fileMappingsLength + index))
    }

    const handleSelectChange = value => {
      if (value.isPrimaryKey) {
        setOpenDialog(true)
        setNewOptional(value)
      } else {
        dispatch(setOptionalMappings(fileType, guid, value))
      }
    }

    const handleDialogClose = value => {
      setOpenDialog(false)
      dispatch(setOptionalMappings(fileType, guid, newOptional))
    }

    return (
      <TableRow key={key}>
        <TableCell>
          {access &&
            <IconButton
              size="large"
              onClick={handleRemove}
            >
              <DeleteIcon data-testid="DeleteIcon" />
            </IconButton>
          }
        </TableCell>

        <TableCell></TableCell>

        <TableCell>
          <Select
            name={`opt-col-${guid}-${index}`}
            value=''
            placeholder=""
            options={options}
            menuPlacement='auto'
            styles={SingleValueStyles}
            components={{ SingleValue }}
            onChange={handleSelectChange}
          />
        </TableCell>

        <StyledTableCell>
          Select an optional column.
        </StyledTableCell>
        <MessageDialog
          open={openDialog}
          title={DIALOG_TITLE}
          text={DIALOG_TEXT}
          onClose={() => handleDialogClose()}
        />
      </TableRow>
    )
  })
}

const MappingList = ({
  access,
  fileData,
  fileMapping,
  setSourceFocus
}) => {
  const project = useSelector(store => store.project)
  const navigation = useSelector(store => store.navigation)

  const guid = navigation.file.guid
  const fileType = FILE_TYPE[navigation.file?.fileType]
  const studyTypeKey = STUDY_TYPE[project.studyType].toUpperCase()

  if (!fileType) {
    return EMPTY_ROW_ELEMENT
  }

  const optionalColumns = fileType === 'inforce+termination'
    ? COLUMNS_RGA
    : COLUMNS_RGA.filter(
      column => column.required_for_filetype.length === 0 || (
        column.output_type.toUpperCase().includes(studyTypeKey) && (
          column.optionalFileType && column.optionalFileType
            .toUpperCase()
            .includes(fileType.toUpperCase())
        )
      )
    )

  const previouslyMappedOptionalColumns = fileMapping.columns.length
    ? fileMapping.columns.map(column => column.targetColumnName)
    : EMPTY_ARRAY

  const optColumns = optionalColumns.filter(col =>
    !previouslyMappedOptionalColumns.includes(col.name)
  )

  const optionalOptions = optColumns.map((column, index) => ({
    key: column.name,
    value: index,
    isPrimaryKey: column.isPrimaryKey,
    targetDataType: mapDataType(column.type),
    label: column.label,
    toolTip: column.description
  }))

  return (
    <>
      <CoreRows
        access={access}
        guid={guid}
        fileType={fileType}
        fileData={fileData}
        mappingColumns={fileMapping.columns}
        setSourceFocus={setSourceFocus}
      />

      {fileMapping.optionalColumnCount
        ? (
        <OptionalRows
          access={access}
          guid={guid}
          fileType={fileType}
          columnCount={fileMapping.optionalColumnCount}
          options={optionalOptions}
          fileMappingsLength={fileMapping.columns.length}
        />
          )
        : ''}
    </>
  )
}

export default React.memo(MappingList)
